import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import { Edit } from '@mui/icons-material'

import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { LoadingButton } from '@mui/lab'
import { AxiosError } from 'axios'
import { readableAxiosError } from '../../../utils/axios'
import { spacingItem } from '../../theme'

import { GENERIC_DISPLAY } from '../../../utils/genericDisplay'
import { useCanEditAnimal } from '../../../store/useCanEditAnimal'
import { AnimalDto, AnimalDtoHairLength } from '../../../interactors/gen/backendClient'
import { useFormExtended } from '../../../utils/hooks/useFormExtended'
import { getReadableAnimalHairLength, getReadableAnimalFieldLabel } from '../../../domain/Animal/AnimalDisplay'
import { ControlledSelectField } from '../../common/ControlledSelectField'
import { useEditAnimalMutation } from '../../../store/useEditAnimalMutation'

interface Props {
  title: string
  animal: AnimalDto
  renderButton?: (onClick: () => void) => any
}

interface FormParams {
  color: string
  hairLength?: AnimalDtoHairLength
  description?: string
}

/**
 * For now, supposed to be used in the Animal Page, update support if you want to use outside
 */
export const EditorPromptWithFields: FC<Props> = ({ title, renderButton, animal }) => {
  const canEditAnimal = useCanEditAnimal()
  const editAnimalMutation = useEditAnimalMutation({})

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const dialogOpenedState = useDialogOpened()
  const [serverError, setServerError] = useState('')

  const onSave = async (data: FormParams) => {
    setIsLoading(true)

    await editAnimalMutation
      .mutateAsync({
        ...animal,
        color: data.color,
        hairLength: data.hairLength,
        description: data.description,
      })
      .then(async () => {
        setIsLoading(false)
        dialogOpenedState.closeDialog()
      })
      .catch((error: Error | AxiosError) => {
        setServerError(readableAxiosError(error).join(' '))
        setIsLoading(false)
      })
  }

  const getDefaultValues = (animal: AnimalDto): FormParams => ({
    color: animal?.color || '',
    hairLength: animal?.hairLength,
    description: animal?.description || '',
  })

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormExtended(animal, getDefaultValues)

  const description = watch('description')
  const onEditDescription = (newDescription: string) => {
    setValue('description', newDescription)
  }

  const fieldSx = {
    '& .MuiInputLabel-root': {
      fontSize: 12,
      fontWeight: 400,
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      fontSize: 11.5,
      fontWeight: 400,
    },
    '& .MuiOutlinedInput-root': {
      minHeight: 44,
      borderRadius: 3,
      backgroundColor: '#FFFFFF',
    },
    '& .MuiOutlinedInput-input, & .MuiSelect-select': {
      fontSize: 14,
      fontWeight: 400,
    },
    '& .MuiInputBase-input::placeholder': {
      fontSize: 12,
      fontWeight: 300,
      opacity: 1,
    },
  }
  return (
    <>
      {renderButton ? (
        renderButton(dialogOpenedState.openDialog)
      ) : (
        <IconButton color="primary" onClick={dialogOpenedState.openDialog}>
          <Tooltip title="Editer">
            <>
              <Edit />
            </>
          </Tooltip>
        </IconButton>
      )}

      <Dialog
        maxWidth="xl"
        sx={{ marginX: 'auto' }}
        open={dialogOpenedState.isDialogOpen}
        onClose={dialogOpenedState.closeDialog}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: '1px solid #E4E8F2',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 18px 42px rgba(32, 56, 100, 0.12)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: { xs: '1rem', md: '1.08rem' },
            fontWeight: 800,
            color: '#203864',
          }}
        >
          {title}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              border: '1px solid rgba(228, 232, 242, 0.9)',
              borderRadius: 3,
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
              '& .ql-container': {
                minHeight: { xs: 180, md: 220 },
                border: 'none',
                fontSize: 14,
              },
              '& .ql-editor': {
                minHeight: { xs: 180, md: 220 },
                fontSize: 14,
                lineHeight: 1.5,
                color: '#203864',
              },
              '& .ql-toolbar.ql-snow': {
                border: 'none',
                borderBottom: '1px solid rgba(228, 232, 242, 0.9)',
                backgroundColor: '#F9FBFF',
              },
              '& .ql-container.ql-snow': {
                border: 'none',
              },
            }}
          >
            <ReactQuill theme="snow" value={description} onChange={onEditDescription} />
          </Box>

          <form id="editor-form" onSubmit={handleSubmit(onSave)}>
            <ControlledSelectField
              sx={{ ...spacingItem, ...fieldSx }}
              control={control}
              error={errors.hairLength}
              fieldName="hairLength"
              label="Type de poils"
              options={Object.values(AnimalDtoHairLength).map((value: AnimalDtoHairLength) => ({
                label: getReadableAnimalHairLength(value),
                value: value,
              }))}
              requiredRule={undefined}
            />

            {(['color'] as const).map((fieldName) => (
              <TextField
                id={`${fieldName}-input`}
                key={fieldName}
                label={getReadableAnimalFieldLabel(fieldName)}
                type="text"
                fullWidth
                {...register(fieldName)}
                sx={{ ...spacingItem, ...fieldSx }}
              />
            ))}
          </form>

          {serverError && (
            <Box sx={spacingItem}>
              <Typography color="error">{serverError}</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Tooltip title={!canEditAnimal ? GENERIC_DISPLAY.doNotHavePermissionAsReader : ''}>
            <Box component="span" sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
              <LoadingButton
                variant="outlined"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minHeight: 38,
                  px: 2.5,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12.5,
                  fontWeight: 700,
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  borderColor: 'rgba(122, 129, 151, 0.24)',
                  color: '#203864',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    borderColor: 'rgba(122, 129, 151, 0.35)',
                    boxShadow: 'none',
                  },
                }}
                loading={isLoading}
                type="submit"
                disabled={!canEditAnimal}
                form="editor-form"
              >
                Mettre à jour
              </LoadingButton>
            </Box>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  )
}
